import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  CircularProgress,
  Box,
  Collapse,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Alert,
  MenuItem,
  TextField,
  IconButton,
  Button,
  Slider,
  Paper,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import StraightenIcon from "@mui/icons-material/Straighten";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import TopNavBar from "../components/TopNavBar";
import ZoneGroupIcon from "../components/ZoneGroupIcon";
import {
  getDeviceZoneGroups,
  getDeviceZones,
  createOrUpdateZoneGroup,
  updateZoneGroupStatus,
  deleteZoneGroup,
} from "../api/maps";
import {
  getDeviceCleaningConfigs,
  getDeviceAvailableConfigs,
  createOrUpdateCleaningConfigs,
} from "../api/cleaning";
import { useAuth } from "../context/AuthContext";

export default function DeviceCleaningConfigurationPage() {
  const { user } = useAuth();
  const username = user?.username;
  const navigate = useNavigate();
  const [zoneGroupData, setZoneGroupData] = useState(null);
  const [zoneData, setZoneData] = useState([]);
  const [cleaningConfigData, setCleaningConfigData] = useState([]);
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [groupZones, setGroupZones] = useState({});
  const [pendingZoneByGroup, setPendingZoneByGroup] = useState({});
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [newGroupDraft, setNewGroupDraft] = useState({
    zone_group_name: "",
    zone_group_display_name: "",
    zone_group_display_name_dk: "",
  });
  const [dirtyGroups, setDirtyGroups] = useState({});
  const [originalGroupData, setOriginalGroupData] = useState({});
  const [originalGroupZones, setOriginalGroupZones] = useState({});
  const [availableConfigs, setAvailableConfigs] = useState([]);
  const [editableCleaningLevels, setEditableCleaningLevels] = useState({});
  const [cleaningConfigDrafts, setCleaningConfigDrafts] = useState({});
  const [savingCleaningLevel, setSavingCleaningLevel] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { deviceId } = useParams();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Snackbar helper functions
  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatTime = (minutes) => {
    if (minutes == null || minutes === 0) return "-";
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  };

  const formatDistance = (meters) => {
    if (meters == null || meters === 0) return "-";
    return meters >= 1000 ? `${(meters / 1000).toFixed(1)}km` : `${meters}m`;
  };

  const formatArea = (area) => {
    if (area == null || area === 0) return "-";
    return `${area} m²`;
  };

  const buildInitialGroupZones = useCallback((groups, zones) => {
    const zonesById = new Map(
      (zones || []).map((zone) => [zone.zone_id, zone]),
    );
    const result = {};

    (groups || []).forEach((group) => {
      const zoneIds = (group.zone_id_sequence || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);

      result[group.zone_group_pk_id] = zoneIds
        .map((zoneId) => zonesById.get(zoneId))
        .filter(Boolean);
    });

    return result;
  }, []);

  const buildInitialGroupDataMap = useCallback((groups) => {
    return (groups || []).reduce((acc, group) => {
      acc[group.zone_group_pk_id] = { ...group };
      return acc;
    }, {});
  }, []);

  const getGroupMetrics = useCallback(
    (group) => {
      const zones = groupZones[group.zone_group_pk_id] || [];
      if (zones.length === 0) {
        return {
          totalZones: 0,
          totalTime: 0,
          totalDistance: 0,
          totalArea: 0,
          executionRange: "N/A - N/A",
        };
      }

      const totals = zones.reduce(
        (acc, zone) => {
          acc.totalTime += Number(zone.estimated_time || 0);
          acc.totalDistance += Number(zone.distance || 0);
          acc.totalArea += Number(zone.area || 0);
          return acc;
        },
        { totalTime: 0, totalDistance: 0, totalArea: 0 },
      );

      return {
        totalZones: zones.length,
        totalTime: totals.totalTime,
        totalDistance: totals.totalDistance,
        totalArea: totals.totalArea,
        executionRange: `1 - ${zones.length}`,
      };
    },
    [groupZones],
  );

  const moveZone = (groupId, index, direction) => {
    setGroupZones((prev) => {
      const current = [...(prev[groupId] || [])];
      const target = index + direction;
      if (target < 0 || target >= current.length) {
        return prev;
      }
      [current[index], current[target]] = [current[target], current[index]];
      setDirtyGroups((prevDirty) => ({ ...prevDirty, [groupId]: true }));
      return { ...prev, [groupId]: current };
    });
  };

  const removeZone = (groupId, zoneId) => {
    setDirtyGroups((prevDirty) => ({ ...prevDirty, [groupId]: true }));
    setGroupZones((prev) => ({
      ...prev,
      [groupId]: (prev[groupId] || []).filter(
        (zone) => zone.zone_id !== zoneId,
      ),
    }));
  };

  const addZone = (group, zoneId) => {
    if (!zoneId) return;
    const groupId = group.zone_group_pk_id;
    const current = groupZones[groupId] || [];
    const existingIds = new Set(current.map((zone) => zone.zone_id));
    if (existingIds.has(zoneId)) return;

    const selectedZone = zoneData.find(
      (zone) => zone.zone_id === zoneId && zone.map_name === group.map_name,
    );
    if (!selectedZone) return;

    setGroupZones((prev) => ({
      ...prev,
      [groupId]: [...(prev[groupId] || []), selectedZone],
    }));
    setDirtyGroups((prevDirty) => ({ ...prevDirty, [groupId]: true }));

    setPendingZoneByGroup((prev) => ({
      ...prev,
      [groupId]: "",
    }));
  };

  const updateGroupNameField = (groupId, field, value) => {
    setZoneGroupData((prev) =>
      (prev || []).map((group) =>
        group.zone_group_pk_id === groupId
          ? { ...group, [field]: value }
          : group,
      ),
    );
    setDirtyGroups((prevDirty) => ({ ...prevDirty, [groupId]: true }));
  };

  const createNewZoneGroup = () => {
    if (!newGroupDraft.zone_group_display_name.trim()) {
      return;
    }

    const baseMapName = (zoneGroupData && zoneGroupData[0]?.map_name) || "";
    const newGroupId = `new_${Date.now()}`;
    const nextGroup = {
      zone_group_pk_id: newGroupId,
      zone_group_name:
        newGroupDraft.zone_group_name.trim() ||
        newGroupDraft.zone_group_display_name
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "_"),
      zone_group_display_name: newGroupDraft.zone_group_display_name.trim(),
      zone_group_display_name_dk:
        newGroupDraft.zone_group_display_name_dk.trim(),
      map_name: baseMapName,
      total_zones_in_group: 0,
      total_group_time: 0,
      total_group_distance: 0,
      total_group_area: 0,
      first_execution_order: null,
      last_execution_order: null,
      is_new: true,
    };

    setZoneGroupData((prev) => [nextGroup, ...(prev || [])]);
    setGroupZones((prev) => ({ ...prev, [newGroupId]: [] }));
    setCreatingGroup(false);
    setNewGroupDraft({
      zone_group_name: "",
      zone_group_display_name: "",
      zone_group_display_name_dk: "",
    });
    setExpandedGroupId(newGroupId);
    setDirtyGroups((prevDirty) => ({ ...prevDirty, [newGroupId]: true }));
  };

  const handleSaveGroupChanges = async (groupId) => {
    const targetGroup = (zoneGroupData || []).find(
      (group) => group.zone_group_pk_id === groupId,
    );
    if (!targetGroup) {
      return;
    }

    const isEnglishNameEmpty = !String(
      targetGroup.zone_group_display_name || "",
    ).trim();
    const isDanishNameEmpty = !String(
      targetGroup.zone_group_display_name_dk || "",
    ).trim();

    if (isEnglishNameEmpty && isDanishNameEmpty) {
      window.alert(
        "Please provide at least an English or Danish zone group name before saving.",
      );
      return;
    }

    const payload = {
      deviceId,
      zoneGroup: {
        zone_group_pk_id: targetGroup.zone_group_pk_id,
        zone_group_name: targetGroup.zone_group_name,
        zone_group_display_name: targetGroup.zone_group_display_name,
        zone_group_display_name_dk: targetGroup.zone_group_display_name_dk,
        map_name: targetGroup.map_name,
        is_new: Boolean(targetGroup.is_new),
        zones: (groupZones[targetGroup.zone_group_pk_id] || []).map(
          (zone, index) => ({
            zone_pk_id: zone.zone_pk_id,
            zone_id: zone.zone_id,
            execution_order: index + 1,
          }),
        ),
      },
    };

    console.log("Save zone-group changes payload:", payload);
    try {
      const result = await createOrUpdateZoneGroup(payload);
      if (result.success) {
        showSnackbar("Zone group updated successfully", "success");
      } else {
        showSnackbar(`Failed to update zone group: ${result.error}`, "error");
      }
    } catch (error) {
      showSnackbar(`Error updating zone group: ${error.message}`, "error");
    }

    setOriginalGroupData((prev) => ({
      ...prev,
      [groupId]: { ...targetGroup },
    }));
    setOriginalGroupZones((prev) => ({
      ...prev,
      [groupId]: [...(groupZones[groupId] || [])],
    }));
    setDirtyGroups((prevDirty) => ({ ...prevDirty, [groupId]: false }));
  };

  const handleCancelGroupChanges = (groupId) => {
    const currentGroup = (zoneGroupData || []).find(
      (group) => group.zone_group_pk_id === groupId,
    );
    const originalGroup = originalGroupData[groupId];

    if (currentGroup?.is_new && !originalGroup) {
      setZoneGroupData((prev) =>
        (prev || []).filter((group) => group.zone_group_pk_id !== groupId),
      );
      setGroupZones((prev) => {
        const next = { ...prev };
        delete next[groupId];
        return next;
      });
      setPendingZoneByGroup((prev) => {
        const next = { ...prev };
        delete next[groupId];
        return next;
      });
      if (expandedGroupId === groupId) {
        setExpandedGroupId(null);
      }
    } else if (originalGroup) {
      setZoneGroupData((prev) =>
        (prev || []).map((group) =>
          group.zone_group_pk_id === groupId ? { ...originalGroup } : group,
        ),
      );
      setGroupZones((prev) => ({
        ...prev,
        [groupId]: [...(originalGroupZones[groupId] || [])],
      }));
      setPendingZoneByGroup((prev) => ({
        ...prev,
        [groupId]: "",
      }));
    }

    setDirtyGroups((prevDirty) => ({ ...prevDirty, [groupId]: false }));
  };

  const handleToggleGroupActive = async (group) => {
    const groupId = group.zone_group_pk_id;

    if (String(groupId).startsWith("new_")) {
      setZoneGroupData((prev) =>
        (prev || []).map((item) =>
          item.zone_group_pk_id === groupId
            ? { ...item, active: Number(item.active) === 1 ? 0 : 1 }
            : item,
        ),
      );
      setDirtyGroups((prevDirty) => ({ ...prevDirty, [groupId]: true }));
      return;
    }

    try {
      const nextActive = Number(group.active) === 1 ? 0 : 1;
      const result = await updateZoneGroupStatus({
        deviceId,
        zoneGroupPkId: groupId,
        active: nextActive,
      });
      if (result.success) {
        setZoneGroupData((prev) =>
          (prev || []).map((item) =>
            item.zone_group_pk_id === groupId
              ? { ...item, active: nextActive }
              : item,
          ),
        );

        showSnackbar(
          `Zone group ${nextActive === 1 ? "activated" : "deactivated"} successfully`,
          "success",
        );
      } else {
        showSnackbar(
          `Failed to toggle zone group status: ${result.error}`,
          "error",
        );
      }
    } catch (err) {
      showSnackbar(
        `Failed to toggle zone group status: ${err.message}`,
        "error",
      );
    }
  };

  const handleDeleteGroup = async (group) => {
    const groupId = group.zone_group_pk_id;
    const confirmed = window.confirm(
      `Delete zone group '${group.zone_group_display_name || group.zone_group_name}'?`,
    );

    if (!confirmed) {
      return;
    }

    if (String(groupId).startsWith("new_")) {
      handleCancelGroupChanges(groupId);
      showSnackbar("New unsaved group removed", "success");
      return;
    }

    try {
      const result = await deleteZoneGroup({
        deviceId,
        zoneGroupPkId: groupId,
      });

      if (result.success) {
        setZoneGroupData((prev) =>
          (prev || []).filter((item) => item.zone_group_pk_id !== groupId),
        );
        setGroupZones((prev) => {
          const next = { ...prev };
          delete next[groupId];
          return next;
        });
        setPendingZoneByGroup((prev) => {
          const next = { ...prev };
          delete next[groupId];
          return next;
        });
        if (expandedGroupId === groupId) {
          setExpandedGroupId(null);
        }

        showSnackbar("Zone group deleted successfully", "success");
      } else {
        showSnackbar(`Failed to delete zone group: ${result.error}`, "error");
      }
    } catch (err) {
      showSnackbar(`Failed to delete zone group: ${err.message}`, "error");
    }
  };

  const getDefaultFrontendNameForLevel = (level) => {
    //TO DO - Retrieve the name from the database in future and add translations field. IT may also be a reuirement to add more settings, i.e. 1-4.
    if (level === 1) return "Quick";
    if (level === 2) return "Normal";
    if (level === 3) return "Intense";
    return `Level ${level}`;
  };

  const handleToggleCleaningLevelEdit = (level, sliderRows) => {
    const isEditing = Boolean(editableCleaningLevels[level]);
    if (isEditing) {
      setEditableCleaningLevels((prev) => ({ ...prev, [level]: false }));
      return;
    }

    const draftForLevel = sliderRows.reduce((acc, row) => {
      acc[row.configName] = row.value;
      return acc;
    }, {});

    setCleaningConfigDrafts((prev) => ({ ...prev, [level]: draftForLevel }));
    setEditableCleaningLevels((prev) => ({ ...prev, [level]: true }));
  };

  const handleCleaningSliderChange = (level, configName, nextValue) => {
    setCleaningConfigDrafts((prev) => ({
      ...prev,
      [level]: {
        ...(prev[level] || {}),
        [configName]: nextValue,
      },
    }));
  };

  const isCleaningLevelDirty = (level, sliderRows) => {
    const draft = cleaningConfigDrafts[level] || {};
    return sliderRows.some((row) => {
      const draftValue = draft[row.configName];
      if (draftValue === undefined || draftValue === null) {
        return false;
      }
      return Number(draftValue) !== Number(row.value);
    });
  };

  const handleCancelCleaningLevelChanges = (level) => {
    setCleaningConfigDrafts((prev) => {
      const next = { ...prev };
      delete next[level];
      return next;
    });
    setEditableCleaningLevels((prev) => ({ ...prev, [level]: false }));
  };

  const handleSaveCleaningLevelChanges = async (levelCard) => {
    const level = levelCard.level;
    const draft = cleaningConfigDrafts[level] || {};
    const isFirstSave = levelCard.sliderRows.every((row) => !row.pkId);
    const levelRows = (cleaningConfigData || []).filter(
      (cfg) => Number(cfg.cleaning_level) === Number(level),
    );
    const existingByName = levelRows.reduce((acc, row) => {
      if (row?.config_name) {
        acc[row.config_name] = row;
      }
      return acc;
    }, {});

    const rowsToUpdate = levelCard.sliderRows
      .map((row) => {
        const nextValue = draft[row.configName];
        if (nextValue === undefined || nextValue === null) {
          return null;
        }

        if (Number(nextValue) === Number(row.value)) {
          return null;
        }

        return {
          configName: row.configName,
          value: Number(nextValue),
          pkId: row.pkId || null,
          existing: existingByName[row.configName] || null,
        };
      })
      .filter(Boolean);

    if (!isFirstSave && rowsToUpdate.length === 0) {
      showSnackbar("No changes to save", "info");
      setEditableCleaningLevels((prev) => ({ ...prev, [level]: false }));
      return;
    }

    setSavingCleaningLevel((prev) => ({ ...prev, [level]: true }));
    try {
      await createOrUpdateCleaningConfigs({
        rc_device_id: deviceId,
        first_save: isFirstSave,
        entries: isFirstSave
          ? levelCard.sliderRows.map((row) => ({
              frontend_name:
                levelCard.frontendName || getDefaultFrontendNameForLevel(level),
              cleaning_level: level,
              config_name: row.configName,
              config_value: String(
                (cleaningConfigDrafts[level] || {})[row.configName] ??
                  row.value,
              ),
              active: true,
            }))
          : rowsToUpdate.map((row) => ({
              pk_id: row.pkId,
              config_value: String(row.value),
            })),
      });

      showSnackbar(`Cleaning level ${level} updated successfully`, "success");
      setEditableCleaningLevels((prev) => ({ ...prev, [level]: false }));
      setCleaningConfigDrafts((prev) => {
        const next = { ...prev };
        delete next[level];
        return next;
      });
      await fetchDeviceCleaningData();
    } catch (saveError) {
      showSnackbar(
        `Failed to save cleaning level ${level}: ${saveError.message}`,
        "error",
      );
    } finally {
      setSavingCleaningLevel((prev) => ({ ...prev, [level]: false }));
    }
  };

  const fetchDeviceCleaningData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log("Fetching cleaning configuration for device ID:", deviceId);
      const [zoneGroupsData, zonesData, configsData, availableConfigsData] =
        await Promise.all([
          getDeviceZoneGroups(deviceId),
          getDeviceZones(deviceId),
          getDeviceCleaningConfigs(deviceId),
          getDeviceAvailableConfigs(deviceId),
        ]);
      const initialGroupZones = buildInitialGroupZones(
        zoneGroupsData,
        zonesData,
      );
      const initialGroupData = buildInitialGroupDataMap(zoneGroupsData);
      setZoneGroupData(zoneGroupsData);
      setZoneData(zonesData);
      setCleaningConfigData(configsData);
      setGroupZones(initialGroupZones);
      setOriginalGroupData(initialGroupData);
      setOriginalGroupZones(initialGroupZones);
      setAvailableConfigs(availableConfigsData);
      console.log("Device zone groups:", zoneGroupsData);
      console.log("Device zones:", zonesData);
      console.log("Device cleaning configs:", configsData);
      console.log("Device available configs:", availableConfigsData);
    } catch (err) {
      console.error("Error loading device cleaning data:", err.message);
      setError("Failed to load device cleaning data");
    } finally {
      setLoading(false);
    }
  }, [buildInitialGroupDataMap, buildInitialGroupZones, deviceId]);

  useEffect(() => {
    fetchDeviceCleaningData();
  }, [fetchDeviceCleaningData]);

  if (loading) {
    return (
      <>
        <TopNavBar
          title="device - RC Service Tool"
          username={username}
          heading={"Cleaning Configuration"}
        />
        <Container sx={{ mt: 4, textAlign: "center" }}>
          <CircularProgress />
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <TopNavBar
          title="device - RC Service Tool"
          username={username}
          heading={"Cleaning Configuration"}
        />
        <Container sx={{ mt: 4 }}>
          <Typography color="error">{error}</Typography>
        </Container>
      </>
    );
  }

  if (!zoneGroupData) {
    return (
      <>
        <TopNavBar
          title="device - RC Service Tool"
          username={username}
          heading={"Cleaning Configuration"}
        />
        <Container sx={{ mt: 4 }}>
          <Typography>Device not found</Typography>
        </Container>
      </>
    );
  }

  const cleaningLevelLabels = {
    1: "Quick",
    2: "Normal",
    3: "Intense",
  };

  const availableConfigByName = (availableConfigs || []).reduce((acc, cfg) => {
    if (cfg?.config_name) {
      acc[cfg.config_name] = cfg;
    }
    return acc;
  }, {});

  const configNames = Array.from(
    new Set([
      ...(availableConfigs || []).map((cfg) => cfg.config_name).filter(Boolean),
      ...(cleaningConfigData || [])
        .map((cfg) => cfg.config_name)
        .filter(Boolean),
    ]),
  );

  const levelCards = [1, 2, 3].map((level) => {
    const levelRows = (cleaningConfigData || []).filter(
      (cfg) => Number(cfg.cleaning_level) === level,
    );
    const configRowByName = levelRows.reduce((acc, row) => {
      if (row?.config_name) {
        acc[row.config_name] = row;
      }
      return acc;
    }, {});

    const sliderRows = configNames.map((name) => {
      const definition = availableConfigByName[name] || {};
      const levelConfig = configRowByName[name];

      const min = Number(definition.minimum_value ?? 0);
      const max = Number(definition.maximum_value ?? 10);
      const step = Number(definition.incremental_value ?? 1) || 1;

      const parsedValue = Number(levelConfig?.config_value ?? min);
      const value = Number.isFinite(parsedValue)
        ? Math.max(min, Math.min(max, parsedValue))
        : min;

      return {
        configName: name,
        pkId: levelConfig?.pk_id || null,
        value,
        min,
        max,
        step,
      };
    });

    return {
      level,
      label: cleaningLevelLabels[level] || `Level ${level}`,
      frontendName: levelRows[0]?.frontend_name || null,
      sliderRows,
    };
  });

  return (
    <>
      <TopNavBar
        title="device - RC Service Tool"
        username={username}
        heading={"Cleaning Configuration"}
      />
      <Container
        sx={{ mt: 4, pb: 4, width: "90%", maxWidth: "1200px", mx: "auto" }}
      >
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h4">Cleaning Configuration</Typography>
          <Chip
            label={deviceId}
            color="secondary"
            variant="outlined"
            onClick={() => navigate(`/device/${deviceId}`)}
          />
        </Box>

        <Paper variant="outlined" sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            aria-label="Device cleaning configuration tabs"
          >
            <Tab label="Cleaning Zones" />
            <Tab label="Cleaning Configs" />
          </Tabs>
        </Paper>

        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {activeTab === 0 && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{ mr: "auto" }}
              onClick={() => setCreatingGroup((prev) => !prev)}
            >
              {creatingGroup ? "Cancel New Group" : "Create Zone Group"}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`/device-controls/${deviceId}`)}
          >
            View Controls
          </Button>
        </Box>

        {activeTab === 0 && (
          <>
            {creatingGroup && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  New Zone Group
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr auto",
                    gap: 1,
                  }}
                >
                  <TextField
                    size="small"
                    label="Internal Name"
                    value={newGroupDraft.zone_group_name}
                    onChange={(e) =>
                      setNewGroupDraft((prev) => ({
                        ...prev,
                        zone_group_name: e.target.value,
                      }))
                    }
                  />
                  <TextField
                    size="small"
                    label="Display Name (English)"
                    value={newGroupDraft.zone_group_display_name}
                    onChange={(e) =>
                      setNewGroupDraft((prev) => ({
                        ...prev,
                        zone_group_display_name: e.target.value,
                      }))
                    }
                  />
                  <TextField
                    size="small"
                    label="Display Name (Danish)"
                    value={newGroupDraft.zone_group_display_name_dk}
                    onChange={(e) =>
                      setNewGroupDraft((prev) => ({
                        ...prev,
                        zone_group_display_name_dk: e.target.value,
                      }))
                    }
                  />
                  <Button variant="contained" onClick={createNewZoneGroup}>
                    Add
                  </Button>
                </Box>
              </Paper>
            )}

            <Grid container spacing={3}>
              {(zoneGroupData || []).map((group) => (
                <Grid
                  size={{ xs: 12, sm: 6, lg: 4 }}
                  key={group.zone_group_pk_id}
                >
                  {(() => {
                    const metrics = getGroupMetrics(group);
                    const groupId = group.zone_group_pk_id;
                    const zonesInGroup = groupZones[groupId] || [];
                    const availableZones = zoneData.filter(
                      (zone) =>
                        zone.map_name === group.map_name &&
                        !zonesInGroup.some(
                          (inGroup) => inGroup.zone_id === zone.zone_id,
                        ),
                    );

                    return (
                      <Card
                        variant="outlined"
                        sx={{
                          height: "100%",
                          transition:
                            "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: 3,
                          },
                        }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              mb: 2,
                            }}
                          >
                            <ZoneGroupIcon
                              groupName={group.zone_group_name}
                              displayName={group.zone_group_display_name}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="h6"
                                component="div"
                                sx={{ fontWeight: 600, mb: 0.5 }}
                              >
                                {group.zone_group_display_name ||
                                  group.zone_group_name}
                              </Typography>
                              {group.zone_group_display_name_dk && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {group.zone_group_display_name_dk}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 1.5,
                              mb: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <GroupWorkIcon
                                sx={{ fontSize: 16, color: "#666" }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {metrics.totalZones} zones
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <AccessTimeIcon
                                sx={{ fontSize: 16, color: "#666" }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {formatTime(metrics.totalTime)}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <StraightenIcon
                                sx={{ fontSize: 16, color: "#666" }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {formatDistance(metrics.totalDistance)}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <SquareFootIcon
                                sx={{ fontSize: 16, color: "#666" }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {formatArea(metrics.totalArea)}
                              </Typography>
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              mt: 2,
                              pt: 2,
                              borderTop: 1,
                              borderColor: "divider",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              Execution Order: {metrics.executionRange}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              mt: 2,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Chip
                              label={group.map_name}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                            <Chip
                              label={
                                Number(group.active) === 1
                                  ? "Active"
                                  : "Inactive"
                              }
                              size="small"
                              color={
                                Number(group.active) === 1
                                  ? "success"
                                  : "default"
                              }
                            />
                            <Button
                              size="small"
                              onClick={() =>
                                setExpandedGroupId((prev) =>
                                  prev === groupId ? null : groupId,
                                )
                              }
                              endIcon={
                                expandedGroupId === groupId ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )
                              }
                            >
                              {expandedGroupId === groupId
                                ? "Collapse"
                                : "Manage"}
                            </Button>
                          </Box>

                          <Collapse
                            in={expandedGroupId === groupId}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box
                              sx={{
                                mt: 2.5,
                                pt: 2.5,
                                borderTop: 1,
                                borderColor: "divider",
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Zone Sequence
                              </Typography>

                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: 1,
                                  mb: 2,
                                }}
                              >
                                <TextField
                                  size="small"
                                  label="English Name"
                                  value={group.zone_group_display_name || ""}
                                  onChange={(e) =>
                                    updateGroupNameField(
                                      groupId,
                                      "zone_group_display_name",
                                      e.target.value,
                                    )
                                  }
                                />
                                <TextField
                                  size="small"
                                  label="Danish Name"
                                  value={group.zone_group_display_name_dk || ""}
                                  onChange={(e) =>
                                    updateGroupNameField(
                                      groupId,
                                      "zone_group_display_name_dk",
                                      e.target.value,
                                    )
                                  }
                                />
                              </Box>

                              {zonesInGroup.length === 0 && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 2 }}
                                >
                                  No zones assigned to this group.
                                </Typography>
                              )}

                              {zonesInGroup.map((zone, index) => (
                                <Box key={zone.zone_id}>
                                  <Box
                                    sx={{
                                      py: 0.75,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {index + 1}.{" "}
                                      {zone.zone_name || zone.zone_id}
                                    </Typography>
                                    <Box>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          moveZone(groupId, index, -1)
                                        }
                                        disabled={index === 0}
                                      >
                                        <ArrowUpwardIcon fontSize="inherit" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          moveZone(groupId, index, 1)
                                        }
                                        disabled={
                                          index === zonesInGroup.length - 1
                                        }
                                      >
                                        <ArrowDownwardIcon fontSize="inherit" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() =>
                                          removeZone(groupId, zone.zone_id)
                                        }
                                      >
                                        <DeleteOutlineIcon fontSize="inherit" />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                  {index < zonesInGroup.length - 1 && (
                                    <Divider />
                                  )}
                                </Box>
                              ))}

                              <Box
                                sx={{
                                  mt: 2,
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "center",
                                }}
                              >
                                <FormControl size="small" fullWidth>
                                  <InputLabel id={`add-zone-label-${groupId}`}>
                                    Add Zone
                                  </InputLabel>
                                  <Select
                                    labelId={`add-zone-label-${groupId}`}
                                    value={pendingZoneByGroup[groupId] || ""}
                                    label="Add Zone"
                                    onChange={(e) =>
                                      setPendingZoneByGroup((prev) => ({
                                        ...prev,
                                        [groupId]: e.target.value,
                                      }))
                                    }
                                  >
                                    {availableZones.map((zone) => (
                                      <MenuItem
                                        key={zone.zone_id}
                                        value={zone.zone_id}
                                      >
                                        {zone.zone_name || zone.zone_id}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<AddCircleOutlineIcon />}
                                  onClick={() =>
                                    addZone(
                                      group,
                                      pendingZoneByGroup[groupId] || "",
                                    )
                                  }
                                  disabled={!pendingZoneByGroup[groupId]}
                                >
                                  Add
                                </Button>
                              </Box>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 1.5, display: "block" }}
                              >
                                Save appears when this group has changes.
                              </Typography>

                              <Box
                                sx={{
                                  mt: 1,
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  gap: 1,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleToggleGroupActive(group)}
                                >
                                  {Number(group.active) === 1
                                    ? "Set Inactive"
                                    : "Set Active"}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleDeleteGroup(group)}
                                >
                                  Delete Group
                                </Button>
                              </Box>

                              {dirtyGroups[groupId] && (
                                <Box
                                  sx={{
                                    mt: 1.5,
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    gap: 1,
                                  }}
                                >
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={() =>
                                      handleSaveGroupChanges(groupId)
                                    }
                                    disabled={
                                      !String(
                                        group.zone_group_display_name || "",
                                      ).trim() &&
                                      !String(
                                        group.zone_group_display_name_dk || "",
                                      ).trim()
                                    }
                                  >
                                    Save Group Changes
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    startIcon={<CloseIcon />}
                                    onClick={() =>
                                      handleCancelGroupChanges(groupId)
                                    }
                                  >
                                    Cancel
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </CardContent>
                      </Card>
                    );
                  })()}
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {activeTab === 1 && (
          <>
            {configNames.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography color="text.secondary">
                  No cleaning configs found.
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {levelCards.map((levelCard) => (
                  <Grid size={{ xs: 12, md: 6, lg: 4 }} key={levelCard.level}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        transition:
                          "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: 3,
                        },
                      }}
                    >
                      <CardContent>
                        {(() => {
                          const isEditing = Boolean(
                            editableCleaningLevels[levelCard.level],
                          );
                          const isSaving = Boolean(
                            savingCleaningLevel[levelCard.level],
                          );
                          const isFirstSaveCard = levelCard.sliderRows.every(
                            (row) => !row.pkId,
                          );
                          const isDirty = isCleaningLevelDirty(
                            levelCard.level,
                            levelCard.sliderRows,
                          );
                          const canSave = isDirty || isFirstSaveCard;

                          return (
                            <>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  mb: 2,
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {levelCard.label}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  {isDirty && (
                                    <Chip
                                      size="small"
                                      color="warning"
                                      label="Edited"
                                    />
                                  )}
                                  <Chip
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    label={`Level ${levelCard.level}`}
                                  />
                                </Box>
                              </Box>

                              <Box
                                sx={{
                                  mb: 1.5,
                                  display: "flex",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleToggleCleaningLevelEdit(
                                      levelCard.level,
                                      levelCard.sliderRows,
                                    )
                                  }
                                  color={isEditing ? "primary" : "default"}
                                >
                                  {isEditing ? (
                                    <LockOpenOutlinedIcon fontSize="small" />
                                  ) : (
                                    <LockOutlinedIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 2,
                                }}
                              >
                                {levelCard.sliderRows.map((sliderRow) => (
                                  <Box
                                    key={`${levelCard.level}_${sliderRow.configName}`}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 0.5,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500 }}
                                      >
                                        {sliderRow.configName}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {isEditing
                                          ? ((cleaningConfigDrafts[
                                              levelCard.level
                                            ] || {})[sliderRow.configName] ??
                                            sliderRow.value)
                                          : sliderRow.value}
                                      </Typography>
                                    </Box>
                                    <Slider
                                      size="small"
                                      value={
                                        isEditing
                                          ? ((cleaningConfigDrafts[
                                              levelCard.level
                                            ] || {})[sliderRow.configName] ??
                                            sliderRow.value)
                                          : sliderRow.value
                                      }
                                      min={sliderRow.min}
                                      max={sliderRow.max}
                                      step={sliderRow.step}
                                      marks={true}
                                      valueLabelDisplay="auto"
                                      disabled={!isEditing || isSaving}
                                      onChange={(_, value) =>
                                        handleCleaningSliderChange(
                                          levelCard.level,
                                          sliderRow.configName,
                                          Number(value),
                                        )
                                      }
                                    />
                                  </Box>
                                ))}
                              </Box>

                              {isEditing && (
                                <Box
                                  sx={{
                                    mt: 2,
                                    pt: 2,
                                    borderTop: 1,
                                    borderColor: "divider",
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    gap: 1,
                                  }}
                                >
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={() =>
                                      handleSaveCleaningLevelChanges(levelCard)
                                    }
                                    disabled={!canSave || isSaving}
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    startIcon={<CloseIcon />}
                                    onClick={() =>
                                      handleCancelCleaningLevelChanges(
                                        levelCard.level,
                                      )
                                    }
                                    disabled={isSaving}
                                  >
                                    Cancel
                                  </Button>
                                </Box>
                              )}
                            </>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
}
